/**
 * "Kört fast?" — the hint ladder (pedagogy spec §4.3.5, B5): principle → set-up →
 * first line, opened one rung at a time with the button or H. The button names the
 * next rung ("Visa ledtråd: Uppställning"). Hints without a level (older content, or
 * a session saved before `hintLevels` existed) fall back to "Visa ledtråd (n/N)".
 * The opened texts are `QuestionResult.hintsRevealed`, whose length the page sends
 * as `answer.hintsUsed` (learning-evidence step 3). Once the solution is shown the
 * ladder offers no further rung: a hint opened then would only mark the attempt
 * hint-assisted for nothing (decision 2026-09-24); opened hints stay visible.
 *
 * Every levelled ladder names its rungs, whatever the item's source. (Test-bank
 * items briefly showed plain numbered hints while their rung labels were being
 * relabelled, GitHub issue #2; that interim rule is gone.) A hint that belongs to
 * one part of the item names it in the item's own style — "Uppställning (b)",
 * "First line (b)" — so rungs of different parts do not read as one climb; a
 * whole-item hint keeps the bare rung name.
 */
import clsx from "clsx";
import { Lightbulb } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import type { Localized as LocalizedValue, PublicHintLevel, PublicQuestion } from "@shared/types";
import { Localized } from "@client/components/Localized";
import { Button, Kbd } from "@client/components/ui";
import { useT } from "@client/i18n";

export interface HintLadderProps {
  question: PublicQuestion;
  /** Hint texts opened so far, in order (`QuestionResult.hintsRevealed`). */
  revealed: readonly LocalizedValue[];
  /** Opens the next hint (the page's `revealHint`, also bound to H). */
  onRevealNext: () => void;
  loading?: boolean;
  /** A plan step waits for its pick: the ladder stays shut (pedagogy spec §4.3.5). */
  locked?: boolean;
  /**
   * The solution is visible (Klar/give-up, or a graded answer): the next-rung button
   * is gone, and with no hint opened the ladder is not shown at all.
   */
  solutionShown?: boolean;
}

const UNLEVELLED: PublicHintLevel = { level: null, partId: null };

/**
 * One step per hint. A question stored in `sessionStorage` before hint levels
 * existed has no `hintLevels`; its hints count as unlevelled.
 */
export function ladderSteps(
  question: Pick<PublicQuestion, "hintCount" | "hintLevels">,
): PublicHintLevel[] {
  const levels = (question.hintLevels as readonly PublicHintLevel[] | undefined) ?? [];
  return Array.from({ length: question.hintCount }, (_, i) => levels[i] ?? UNLEVELLED);
}

/** True when every hint names its rung, so the ladder can show the levels. */
export function isLevelledLadder(steps: readonly PublicHintLevel[]): boolean {
  return steps.length > 0 && steps.every((step) => step.level !== null);
}

export function HintLadder({
  question,
  revealed,
  onRevealNext,
  loading,
  locked,
  solutionShown,
}: HintLadderProps) {
  const t = useT();
  const headingId = useId();
  const lockedNoteId = useId();
  const steps = ladderSteps(question);
  // UX-03: opening the *last* rung removes the "Visa ledtråd" button from the
  // page (the block below only renders it while a rung remains) — the button
  // that had focus disappears, and without this focus would fall to
  // `<body>`. Every earlier rung keeps the button in place, where the
  // `Button` loading fix already keeps focus put.
  const lastHintRef = useRef<HTMLDivElement>(null);
  const prevRevealedCount = useRef(revealed.length);
  useEffect(() => {
    const openedLastRung =
      revealed.length > prevRevealedCount.current && revealed.length === steps.length;
    prevRevealedCount.current = revealed.length;
    if (openedLastRung) lastHintRef.current?.focus();
  }, [revealed.length, steps.length]);
  if (steps.length === 0 || (solutionShown && revealed.length === 0)) return null;

  // Whether the rung list (and each opened hint's rung label) is shown at all:
  // only where every hint names its rung; otherwise the counter button below.
  const levelled = isLevelledLadder(steps);
  const next = revealed.length;
  // With the solution shown no rung is next: nothing is marked current.
  const current = solutionShown ? -1 : next;
  const levelName = (i: number): string => {
    const level = steps[i]?.level;
    return level ? t(`practice.hint.level.${level}`) : "";
  };
  // A part hint names its part after the rung ("Första raden (b)"); the whole
  // item's hints carry the bare rung name.
  const stepName = (i: number): string => {
    const name = levelName(i);
    const partId = steps[i]?.partId;
    return partId ? `${name} (${partId})` : name;
  };

  return (
    <section aria-labelledby={headingId} className="surface mt-4 p-4">
      <h3
        id={headingId}
        className="flex items-center gap-2 text-sm font-semibold text-ink-900 dark:text-ink-50"
      >
        <Lightbulb size={16} aria-hidden="true" className="text-accent-700 dark:text-accent-300" />
        {t("practice.hint.title")}
      </h3>

      {levelled && (
        <ol className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {steps.map((_, i) => (
            <li
              key={i}
              aria-current={i === current ? "step" : undefined}
              className={clsx(
                i < next && "font-semibold text-accent-800 dark:text-accent-200",
                i === current && "font-medium text-ink-900 dark:text-ink-50",
                i >= next && i !== current && "text-ink-600 dark:text-ink-400",
              )}
            >
              <span aria-hidden="true">{i + 1}</span> {stepName(i)}
            </li>
          ))}
        </ol>
      )}

      <div aria-live="polite" className={clsx("space-y-2", revealed.length > 0 && "mt-3")}>
        {revealed.map((hint, i) => (
          <div
            key={i}
            ref={i === revealed.length - 1 ? lastHintRef : undefined}
            tabIndex={-1}
            role="note"
            className="rounded-lg border border-accent-200 bg-accent-50 p-3 text-sm outline-none dark:border-accent-800 dark:bg-accent-950/60"
          >
            {levelled && (
              <p className="mb-1 text-xs font-semibold text-accent-800 dark:text-accent-200">
                {stepName(i)}
              </p>
            )}
            <Localized value={hint} variant="compact" />
          </div>
        ))}
      </div>

      {next < steps.length && !solutionShown && (
        <Button
          variant="secondary"
          size="sm"
          className="mt-3 max-sm:min-h-11"
          iconLeft={<Lightbulb size={14} aria-hidden="true" />}
          onClick={onRevealNext}
          loading={loading}
          disabled={locked}
          aria-describedby={locked ? lockedNoteId : undefined}
        >
          {levelled
            ? t("practice.hint.showLevel", { level: stepName(next) })
            : `${t("practice.hint.show")} (${next + 1}/${steps.length})`}{" "}
          <Kbd className="ml-1">H</Kbd>
        </Button>
      )}

      {locked && (
        <p id={lockedNoteId} className="mt-3 text-xs font-medium text-ink-700 dark:text-ink-300">
          {t("practice.hint.afterPlan")}
        </p>
      )}
      {!solutionShown && (
        <p className="mt-3 text-xs text-ink-600 dark:text-ink-400">{t("practice.hint.note")}</p>
      )}
    </section>
  );
}
